import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGatedOpen } from "../hooks/usePollingGate";
import { useToleranceMutation } from "../hooks/useToleranceMutation";
import { useValidationToast } from "../hooks/useValidationToast";
import { problemMessage } from "../utils/problemDetail";
import {
  TOLERANCE_FIELDS as FIELDS,
  L_TOLERANCE,
  toleranceSchema,
  type ToleranceFormData,
} from "./closeOutSchemas";
import ToleranceChangeList from "./ToleranceChangeList";

const L = `${L_TOLERANCE}.dialog`;

const ToleranceDialog: React.FC<{
  operationId: string;
  shipperCode: string;
  tolerancePct: number;
  onClose: () => void;
}> = ({ operationId, shipperCode, tolerancePct, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  useGatedOpen(true);
  const onInvalid = useValidationToast();
  const mutation = useToleranceMutation(operationId);
  const schema = useMemo(() => toleranceSchema(t), [t]);
  const form = useForm<ToleranceFormData>({
    resolver: zodResolver(schema),
    defaultValues: { tolerancePct: String(tolerancePct) },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        tolerances: [
          { shipperCompanyCode: shipperCode, tolerancePct: Number(values.tolerancePct) },
        ],
      });
      onClose();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L}.failed`),
      });
    }
  }, onInvalid);

  return (
    <GenericDialog
      open
      testId="tolerance-dialog"
      title={t(`${L}.title`)}
      cancelButtonText={t(`${L}.cancel`)}
      confirmButtonText={t(`${L}.save`)}
      handleCloseDialog={onClose}
      confirmAction={() => void submit()}
      isSubmitting={mutation.isPending}
      elementContent={
        <div className="flex flex-col gap-3">
          <Typography variant="body2" data-testid="tolerance-dialog-for">
            {t(`${L}.for`, { shipper: shipperCode })}
          </Typography>
          <FormProvider {...form}>
            <FormItem<ToleranceFormData, "tolerancePct">
              required
              name={FIELDS.TOLERANCE_PCT}
              type="text"
              label={t(`${L}.field`)}
              testId="tolerance-pct"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
          </FormProvider>
          <Typography variant="subtitle2">{t(`${L_TOLERANCE}.history.title`)}</Typography>
          <ToleranceChangeList operationId={operationId} shipperCode={shipperCode} />
        </div>
      }
    />
  );
};

export default ToleranceDialog;
